import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import { styled } from "@mui/system";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  IconButton,
  Stack,
  TextField,
  Toolbar,
  Radio as MuiRadio,
  Typography,
  RadioGroup as MuiRadioGroup,
  FormControlLabel as MuiFormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Select,
  useTheme,
} from "@mui/material";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { Button, ButtonVariantOption } from "@/components/Button";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { MultiSelect, Select as CSelect } from "@/components/Select";
import { usePrefectureOptions } from "@/features/realEstateReceptionBookFeed/hooks/usePrefectureOptions";
import { useCityOptions } from "@/features/realEstateReceptionBookFeed/hooks/useCityOptions";
import { useCitySelect } from "@/features/realEstateReceptionBookFeed/hooks/useCitySelect";
import { Checkbox, CheckboxGroup } from "@/components/Checkbox";
import {
  makeRangeSchema,
  makeRangeSchemaWithRestrictMinMax,
} from "@/utils/utilsReactHookForms";
import { format, getYear, parse } from "date-fns";
import ja from "date-fns/locale/ja";
import { Label } from "@/components/Label";
import { DateRangePicker } from "@/components/DatePicker";
import { getSearchMaxDate } from "@/features/realEstateReceptionBookFeed/utils/dateTime";
import { BUILDING_VARIANT_OPTIONS } from "@/features/titleSearch/routes/TitleSearch";
import {
  preventNonNumericValueWhenKeyDown,
  preventNonNumericValueWhenPaste,
} from "@/utils/utils";
import Tooltip from "@mui/material/Tooltip";
import { useApiClient } from "../../../hooks/useApiClient";
import { toast } from "react-toastify";
import { HttpStatusCode } from "axios";
import { getMapRealEstateSearchAPI } from "../api/getMapRealEstateSearch";
import { type RealEstateSearchResult } from "../types";
import { useAreaUsePurposeOptions } from "@/hooks/useAreaUsePurposeOptions";
import { getBookTypeFromRealEstateTypeLabel } from "./AcquireMultipleBooksModal";
import { useMapServicePermission } from "@/hooks/useMapServicePermission";
import * as Sentry from "@sentry/react";
import { LatLng } from "leaflet";

// 不動産検索の最大件数
const MAX_SEARCH_RESULT_COUNT = 1_000;

// 不動産種別のマップ
export const REAL_ESTATE_TYPE_MAP = {
  land: { label: "土地", value: "land" },
  condominium: { label: "区分建物", value: "kubun_building" },
  building: { label: "建物", value: "building" },
};

// 容積率で入力可能な最小値、最小値を定義する。
const YOUSEKI_RATIO_MIN = 50;
const YOUSEKI_RATIO_MAX = 1300;
// 建ぺい率で入力可能な最小値、最小値を定義する。
const KENPEI_RATIO_MIN = 30;
const KENPEI_RATIO_MAX = 80;
// 築年数で入力可能な最小値を定義する。
const BUILDING_AGE_MIN = 0;
// 築年数で入力可能な最大値を定義する。
// BEのコードで、現在日時のyearからMAX+1を引いた年をstart_dateとしている。
// datetime()のyearに0以下は指定できないので、最大値は現在日時のyear-2とする。
const BUILDING_AGE_MAX =
  getYear(
    parse(
      format(new Date(), "yyyy-MM-dd", { locale: ja }),
      "yyyy-MM-dd",
      new Date()
    )
  ) - 2;

// Zodスキーマ定義
const FormDataSchema = z.object({
  prefecture: z.array(z.number()).nonempty("都道府県を選択してください"),
  city: z.array(z.number()).nonempty("市区町村を選択してください"),
  address: z.string(),
  areaUsePurpose: z.array(z.number()),
  yousekiRatio: makeRangeSchemaWithRestrictMinMax(
    YOUSEKI_RATIO_MIN,
    YOUSEKI_RATIO_MAX,
    "「以上」は「以下」より小さくしてください"
  ),
  kenpeiRatio: makeRangeSchemaWithRestrictMinMax(
    KENPEI_RATIO_MIN,
    KENPEI_RATIO_MAX,
    "「以上」は「以下」より小さくしてください"
  ),
  realEstateType: z
    .string()
    .trim()
    .refine((value) => value.length > 0, {
      message: "不動産種別を選択してください",
    }),
  landType: z.object({
    farm: z.boolean(),
    factoryStorage: z.boolean(),
    solar: z.boolean(),
  }),
  estimateAcreage: makeRangeSchema("「以上」は「以下」より小さくしてください"),
  price: makeRangeSchema("「以上」は「以下」より小さくしてください"),
  walk: z.number().nullable(),
  buildingType: z.object({
    mansion: z.boolean(),
    apartment: z.boolean(),
    building: z.boolean(),
    jpHotel: z.boolean(),
    shop: z.boolean(),
    hotel: z.boolean(),
    factory: z.boolean(),
  }),
  floorCount: makeRangeSchema("「以上」は「以下」より小さくしてください"),
  buildingVariant: z.number().nullable(),
  buildDateRange: z.tuple([z.date().nullable(), z.date().nullable()]),
  buildingAge: makeRangeSchemaWithRestrictMinMax(
    BUILDING_AGE_MIN,
    BUILDING_AGE_MAX,
    "「以上」は「以下」より小さくしてください"
  ),
  area: makeRangeSchema("「以上」は「以下」より小さくしてください"),
});

// 定義したZodのスキーマをTypescriptの型に変換
export type FormData = z.infer<typeof FormDataSchema>;

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  // ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

export const MapSearchDrawer: React.FC<{
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  searchResult: RealEstateSearchResult[];
  setSearchResult: (searchResult: RealEstateSearchResult[]) => void;
  setViewLatLng: (point: LatLng) => void;
}> = (props) => {
  const { open, setOpen, searchResult, setSearchResult, setViewLatLng } = props;
  const apiClient = useApiClient().apiClient;

  const reactHookForm = useForm<FormData>({
    mode: "all",
    defaultValues: {
      prefecture: [],
      city: [],
      address: "",
      areaUsePurpose: [],
      yousekiRatio: { min: null, max: null },
      kenpeiRatio: { min: null, max: null },
      realEstateType: "",
      landType: { farm: false, factoryStorage: false, solar: false },
      estimateAcreage: { min: null, max: null },
      price: { min: null, max: null },
      walk: null,
      buildingType: {
        apartment: false,
        mansion: false,
        building: false,
        jpHotel: false,
        shop: false,
        hotel: false,
        factory: false,
      },
      floorCount: { min: null, max: null },
      buildingVariant: null,
      buildDateRange: [null, null],
      buildingAge: { min: null, max: null },
      area: { min: null, max: null },
    },
    resolver: zodResolver(FormDataSchema),
  });

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  // 都道府県一覧
  const { prefectureOptions } = usePrefectureOptions();
  // 市区町村一覧
  const { cityParams } = useCityOptions();
  // 用途地域一覧を取得
  const { areaUsePurposeOptions } = useAreaUsePurposeOptions();

  // 築年月の選択可能最大値
  const maxDate = getSearchMaxDate();

  // 都道府県の変更を検知する
  const prefWatch = reactHookForm.watch("prefecture");

  // 選択している都道府県に応じた市区町村リストを取得する
  const { cityOptions } = useCitySelect(
    prefWatch.map((value) => value.toString()),
    cityParams
  );

  // 地図表示権限の判定
  const [hasMapViewingPermission] = useMapServicePermission();

  const theme = useTheme();
  const DrawerContent = (
    <Box sx={{ width: 300 }} m={2} role="presentation">
      <Box>
        <Box sx={{ my: 1 }}>
          <Controller
            control={reactHookForm.control}
            name="prefecture"
            render={({ field: { value, onChange }, fieldState }) => {
              return (
                <>
                  <Label required={true}>都道府県</Label>
                  <CSelect
                    error={Boolean(fieldState.error)}
                    formHelperText={fieldState.error?.message}
                    label="都道府県"
                    value={value.length ? value[0].toString() : ""}
                    onChange={(value) => {
                      onChange(value ? [Number(value)] : []);
                      /* eslint-disable */
                      // @ts-ignore
                      // 市区町村の選択をリセットする
                      reactHookForm.setValue("city", []);
                    }}
                    options={prefectureOptions}
                  />
                </>
              );
            }}
          />
        </Box>
        <Box my={1}>
          <Controller
            control={reactHookForm.control}
            name="city"
            render={({ field: { value, onChange }, fieldState }) => {
              return (
                <>
                  <Label required={true}>市区町村</Label>
                  <CSelect
                    error={Boolean(fieldState.error)}
                    formHelperText={fieldState.error?.message}
                    label="市区町村"
                    value={value.length ? value[0].toString() : ""}
                    onChange={(value) => {
                      onChange(value ? [Number(value)] : []);
                    }}
                    options={cityOptions}
                  />
                </>
              );
            }}
          />
        </Box>
        <Box my={1}>
          <Controller
            control={reactHookForm.control}
            name="address"
            render={({ field: { value, onChange } }) => {
              return (
                <>
                  <Label>大字・町丁目</Label>
                  <TextField
                    label="大字・町丁目"
                    value={value}
                    onChange={onChange}
                    fullWidth={true}
                  />
                </>
              );
            }}
          />
        </Box>
      </Box>
      <Box>
        <Box sx={{ my: 1 }}>
          <Label required={true}>不動産種別</Label>
          <Box>
            <Controller
              control={reactHookForm.control}
              name={"realEstateType"}
              render={({ field: { value, onChange } }) => {
                return (
                  <MuiRadioGroup
                    name="realEstateType"
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                  >
                    <MuiFormControlLabel
                      value={REAL_ESTATE_TYPE_MAP.land.value}
                      control={<MuiRadio />}
                      label={REAL_ESTATE_TYPE_MAP.land.label}
                    />
                    <MuiFormControlLabel
                      value={REAL_ESTATE_TYPE_MAP.condominium.value}
                      control={<MuiRadio />}
                      label={REAL_ESTATE_TYPE_MAP.condominium.label}
                    />
                    <MuiFormControlLabel
                      value={REAL_ESTATE_TYPE_MAP.building.value}
                      control={<MuiRadio />}
                      label={REAL_ESTATE_TYPE_MAP.building.label}
                    />
                  </MuiRadioGroup>
                );
              }}
            />
          </Box>
        </Box>
      </Box>
      <Accordion
        expanded={
          REAL_ESTATE_TYPE_MAP.land.value ===
          reactHookForm.watch("realEstateType")
        }
        square
        sx={{ mb: 1, boxShadow: "none", position: "static" }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ flexDirection: "row-reverse" }}
        >
          <Typography sx={{ color: "blue" }}>土地の条件から探す</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <Box sx={{ my: 1 }}>
            <Label>土地種別</Label>
            <Controller
              name={"landType"}
              control={reactHookForm.control}
              render={({ field: { value, onChange } }) => {
                return (
                  <>
                    <Checkbox
                      label="農地"
                      checked={value.farm}
                      onChange={(checked) => {
                        onChange({ ...value, farm: checked });
                      }}
                    />
                    <Checkbox
                      label="工場・倉庫"
                      checked={value.factoryStorage}
                      onChange={(checked) => {
                        onChange({
                          ...value,
                          factoryStorage: checked,
                        });
                      }}
                    />
                    <Checkbox
                      label="太陽光"
                      checked={value.solar}
                      onChange={(checked) => {
                        onChange({ ...value, solar: checked });
                      }}
                    />
                  </>
                );
              }}
            />
          </Box>
          {/* 
            用途地域（areaUsePurpose）のUIコンポーネント
            データベースと地図の間で相違があるため、一時的に非表示にしています。
            バックエンド機能は維持されているため、将来的に必要になった場合は以下のコードを復活させてください。
          */}
          {/* <Box sx={{ my: 1 }}>
            <Label>用途地域</Label>
            <Controller
              name={"areaUsePurpose"}
              control={reactHookForm.control}
              render={({ field: { value, onChange }, fieldState }) => {
                return (
                  <MultiSelect
                    error={Boolean(fieldState.error)}
                    formHelperText={fieldState.error?.message}
                    label="用途地域"
                    value={value.map((value) => value.toString())}
                    onChange={(value) => {
                      onChange(value.map((value) => Number(value)));
                    }}
                    options={areaUsePurposeOptions}
                    maxSelect={5}
                  />
                );
              }}
            />
          </Box> */}
          <Box sx={{ my: 1 }}>
            <Label>推計地積</Label>
            <Controller
              name={"estimateAcreage"}
              control={reactHookForm.control}
              render={({ field: { value, onChange }, fieldState }) => {
                return (
                  <>
                    <TextField
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      value={
                        value.min === null || value.min === 0 ? "" : value.min
                      }
                      onChange={(event) => {
                        onChange({
                          min:
                            event.target.value === ""
                              ? null
                              : Number(event.target.value),
                          max: value.max,
                        });
                      }}
                      onKeyDown={undefined}
                      onPaste={undefined}
                      sx={{ width: "150px", top: "50%" }}
                      inputProps={{
                        min: 0,
                        type: "number",
                        step: "10",
                      }}
                    />
                    ㎡以上 〜
                    <TextField
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      value={
                        value.max === null || value.max === 0 ? "" : value.max
                      }
                      onChange={(event) => {
                        onChange({
                          min: value.min,
                          max:
                            event.target.value === ""
                              ? null
                              : Number(event.target.value),
                        });
                      }}
                      onKeyDown={undefined}
                      onPaste={undefined}
                      sx={{ width: "150px", top: "50%" }}
                      inputProps={{
                        min: 0,
                        type: "number",
                        step: "10",
                      }}
                    />
                    ㎡以下
                  </>
                );
              }}
            />
          </Box>
          <Box sx={{ my: 1 }}>
            <Label>公示価格</Label>
            <Controller
              name={"price"}
              control={reactHookForm.control}
              render={({ field: { value, onChange }, fieldState }) => {
                return (
                  <>
                    <TextField
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      value={
                        value.min === null || value.min === 0 ? "" : value.min
                      }
                      onChange={(event) => {
                        onChange({
                          min:
                            event.target.value === ""
                              ? null
                              : Number(event.target.value),
                          max: value.max,
                        });
                      }}
                      onKeyDown={undefined}
                      onPaste={undefined}
                      sx={{ width: "150px", top: "50%" }}
                      inputProps={{
                        min: 0,
                        type: "number",
                        step: "10",
                      }}
                    />
                    円/㎡以上 〜
                    <TextField
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      value={
                        value.max === null || value.max === 0 ? "" : value.max
                      }
                      onChange={(event) => {
                        onChange({
                          min: value.min,
                          max:
                            event.target.value === ""
                              ? null
                              : Number(event.target.value),
                        });
                      }}
                      onKeyDown={undefined}
                      onPaste={undefined}
                      sx={{ width: "150px", top: "50%" }}
                      inputProps={{
                        min: 0,
                        type: "number",
                        step: "10",
                      }}
                    />
                    円/㎡以下
                  </>
                );
              }}
            />
          </Box>
          {/* 
            容積率（yousekiRatio）のUIコンポーネント
            データベースと地図の間で相違があるため、一時的に非表示にしています。
            バックエンド機能は維持されているため、将来的に必要になった場合は以下のコードを復活させてください。
          */}
          {/* <Box sx={{ my: 1 }}>
            <Label>容積率</Label>
            <Controller
              name={"yousekiRatio"}
              control={reactHookForm.control}
              render={({
                field: { value, onChange },
                formState,
                fieldState,
              }) => {
                return (
                  <>
                    <TextField
                      error={Boolean(
                        formState.errors.yousekiRatio?.min ?? fieldState.error
                      )}
                      helperText={
                        formState.errors.yousekiRatio?.min?.message ??
                        fieldState.error?.message
                      }
                      value={
                        value.min === null || value.min === 0 ? "" : value.min
                      }
                      onChange={(event) => {
                        onChange({
                          min:
                            event.target.value === ""
                              ? null
                              : Number(event.target.value),
                          max: value.max,
                        });
                      }}
                      onKeyDown={undefined}
                      onPaste={undefined}
                      sx={{ width: "150px", top: "50%" }}
                      inputProps={{
                        min: 50,
                        max: 1300,
                        type: "number",
                        step: "10",
                      }}
                    />
                    %以上 〜
                    <TextField
                      error={Boolean(
                        formState.errors.yousekiRatio?.max ?? fieldState.error
                      )}
                      helperText={
                        formState.errors.yousekiRatio?.max?.message ??
                        fieldState.error?.message
                      }
                      value={
                        value.max === null || value.max === 0 ? "" : value.max
                      }
                      onChange={(event) => {
                        onChange({
                          min: value.min,
                          max:
                            event.target.value === ""
                              ? null
                              : Number(event.target.value),
                        });
                      }}
                      onKeyDown={undefined}
                      onPaste={undefined}
                      sx={{ width: "150px", top: "50%" }}
                      inputProps={{
                        min: 50,
                        max: 1300,
                        type: "number",
                        step: "10",
                      }}
                    />
                    %以下
                  </>
                );
              }}
            />
          </Box> */}
          {/* 
            建ぺい率（kenpeiRatio）のUIコンポーネント
            データベースと地図の間で相違があるため、一時的に非表示にしています。
            バックエンド機能は維持されているため、将来的に必要になった場合は以下のコードを復活させてください。
          */}
          {/* <Box sx={{ my: 1 }}>
            <Label>建ぺい率</Label>
            <Controller
              name={"kenpeiRatio"}
              control={reactHookForm.control}
              render={({
                field: { value, onChange },
                formState,
                fieldState,
              }) => {
                return (
                  <>
                    <TextField
                      error={Boolean(
                        formState.errors.kenpeiRatio?.min ?? fieldState.error
                      )}
                      helperText={
                        formState.errors.kenpeiRatio?.min?.message ??
                        fieldState.error?.message
                      }
                      value={
                        value.min === null || value.min === 0 ? "" : value.min
                      }
                      onChange={(event) => {
                        onChange({
                          min:
                            event.target.value === ""
                              ? null
                              : Number(event.target.value),
                          max: value.max,
                        });
                      }}
                      onKeyDown={undefined}
                      onPaste={undefined}
                      sx={{ width: "150px", top: "50%" }}
                      inputProps={{
                        min: 30,
                        max: 80,
                        type: "number",
                        step: "10",
                      }}
                    />
                    %以上 〜
                    <TextField
                      error={Boolean(
                        formState.errors.kenpeiRatio?.max ?? fieldState.error
                      )}
                      helperText={
                        formState.errors.kenpeiRatio?.max?.message ??
                        fieldState.error?.message
                      }
                      value={
                        value.max === null || value.max === 0 ? "" : value.max
                      }
                      onChange={(event) => {
                        onChange({
                          min: value.min,
                          max:
                            event.target.value === ""
                              ? null
                              : Number(event.target.value),
                        });
                      }}
                      onKeyDown={undefined}
                      onPaste={undefined}
                      sx={{ width: "150px", top: "50%" }}
                      inputProps={{
                        min: 30,
                        max: 80,
                        type: "number",
                        step: "10",
                      }}
                    />
                    %以下
                  </>
                );
              }}
            />
          </Box> */}
          <Box sx={{ my: 1 }}>
            <Label>最寄駅からの徒歩時間</Label>
            <Controller
              name={"walk"}
              control={reactHookForm.control}
              render={({ field: { value, onChange } }) => {
                return (
                  <Select
                    value={value === null ? -1 : value}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                    sx={{ width: "150px", minHeight: "56px" }}
                    variant="outlined"
                  >
                    <MenuItem key={-1} value={-1}>
                      指定なし
                    </MenuItem>
                    {[...Array(60).keys()]
                      .filter((val) => val >= 1)
                      .map((val) => (
                        <MenuItem key={val} value={val}>
                          {val}分以内
                        </MenuItem>
                      ))}
                    <MenuItem key={60} value={60}>
                      60分以上
                    </MenuItem>
                  </Select>
                );
              }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={[
          REAL_ESTATE_TYPE_MAP.condominium.value,
          REAL_ESTATE_TYPE_MAP.building.value,
        ].includes(reactHookForm.watch("realEstateType"))}
        square
        sx={{ mb: 1, boxShadow: "none", position: "static" }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ flexDirection: "row-reverse" }}
        >
          <Typography sx={{ color: "blue" }}>建物の条件から探す</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <Box sx={{ mt: 1 }}>
            <CheckboxGroup label="建物種別">
              <Controller
                control={reactHookForm.control}
                name={"buildingType"}
                render={({ field: { value, onChange } }) => {
                  return (
                    <>
                      <Checkbox
                        label="マンション"
                        checked={value.mansion}
                        onChange={(checked) => {
                          onChange({ ...value, mansion: checked });
                        }}
                      />
                      <Checkbox
                        label="アパート"
                        checked={value.apartment}
                        onChange={(checked) => {
                          onChange({ ...value, apartment: checked });
                        }}
                      />
                      <Checkbox
                        label="ビル"
                        checked={value.building}
                        onChange={(checked) => {
                          onChange({ ...value, building: checked });
                        }}
                      />
                      <Checkbox
                        label="旅館"
                        checked={value.jpHotel}
                        onChange={(checked) => {
                          onChange({ ...value, jpHotel: checked });
                        }}
                      />
                      <Checkbox
                        label="店舗"
                        checked={value.shop}
                        onChange={(checked) => {
                          onChange({ ...value, shop: checked });
                        }}
                      />
                      <Checkbox
                        label="ホテル"
                        checked={value.hotel}
                        onChange={(checked) => {
                          onChange({ ...value, hotel: checked });
                        }}
                      />
                      <Checkbox
                        label="工場"
                        checked={value.factory}
                        onChange={(checked) => {
                          onChange({ ...value, factory: checked });
                        }}
                      />
                    </>
                  );
                }}
              />
            </CheckboxGroup>
          </Box>
          <Box sx={{ my: 1 }}>
            <Label>構造</Label>
            <Controller
              name={"buildingVariant"}
              control={reactHookForm.control}
              render={({ field: { value, onChange } }) => {
                return (
                  <CSelect
                    label={""}
                    options={BUILDING_VARIANT_OPTIONS}
                    onChange={(value) => {
                      onChange(Number(value));
                    }}
                  />
                );
              }}
            />
          </Box>
          <Box sx={{ my: 1 }}>
            <Label>階数</Label>
            <Controller
              control={reactHookForm.control}
              name={"floorCount"}
              render={({ field: { value, onChange }, fieldState }) => {
                return (
                  <>
                    <TextField
                      size="small"
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      value={
                        value.min === null || value.min === 0 ? "" : value.min
                      }
                      onChange={(event) => {
                        onChange({
                          min:
                            event.target.value === ""
                              ? null
                              : Number(event.target.value),
                          max: value.max,
                        });
                      }}
                      onKeyDown={preventNonNumericValueWhenKeyDown}
                      onPaste={preventNonNumericValueWhenPaste}
                      sx={{ width: "90px", top: "50%" }}
                      inputProps={{
                        min: 0,
                        type: "number",
                        step: "1",
                      }}
                    />
                    階以上 〜
                    <TextField
                      size="small"
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      value={
                        value.max === null || value.max === 0 ? "" : value.max
                      }
                      onChange={(event) => {
                        onChange({
                          min: value.min,
                          max:
                            event.target.value === ""
                              ? null
                              : Number(event.target.value),
                        });
                      }}
                      onKeyDown={preventNonNumericValueWhenKeyDown}
                      onPaste={preventNonNumericValueWhenPaste}
                      sx={{ width: "90px", top: "50%" }}
                      inputProps={{
                        min: 0,
                        type: "number",
                        step: "1",
                      }}
                    />
                    階以下
                  </>
                );
              }}
            />
          </Box>
          <Box>
            <Box sx={{ my: 1 }}>
              <Label>推計地積</Label>
              <Controller
                control={reactHookForm.control}
                name={"estimateAcreage"}
                render={({ field: { value, onChange }, fieldState }) => {
                  return (
                    <>
                      <TextField
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        value={
                          value.min === null || value.min === 0 ? "" : value.min
                        }
                        onChange={(event) => {
                          onChange({
                            min:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                            max: value.max,
                          });
                        }}
                        onKeyDown={preventNonNumericValueWhenKeyDown}
                        onPaste={preventNonNumericValueWhenPaste}
                        sx={{ width: "90px", top: "50%" }}
                        inputProps={{
                          min: 0,
                          type: "number",
                          step: "1",
                        }}
                      />
                      ㎡以上 〜
                      <TextField
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        value={
                          value.max === null || value.max === 0 ? "" : value.max
                        }
                        onChange={(event) => {
                          onChange({
                            min: value.min,
                            max:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                          });
                        }}
                        onKeyDown={preventNonNumericValueWhenKeyDown}
                        onPaste={preventNonNumericValueWhenPaste}
                        sx={{ width: "90px", top: "50%" }}
                        inputProps={{
                          min: 0,
                          type: "number",
                          step: "1",
                        }}
                      />
                      ㎡以下
                    </>
                  );
                }}
              />
            </Box>
            <Box sx={{ my: 1 }}>
              <Label>専有面積</Label>
              <Controller
                control={reactHookForm.control}
                name={"area"}
                render={({ field: { value, onChange }, fieldState }) => {
                  return (
                    <>
                      <TextField
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        value={
                          value.min === null || value.min === 0 ? "" : value.min
                        }
                        onChange={(event) => {
                          onChange({
                            min:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                            max: value.max,
                          });
                        }}
                        onKeyDown={preventNonNumericValueWhenKeyDown}
                        onPaste={preventNonNumericValueWhenPaste}
                        sx={{ width: "90px", top: "50%" }}
                        inputProps={{
                          min: 0,
                          type: "number",
                          step: "1",
                        }}
                      />
                      ㎡以上 〜
                      <TextField
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        value={
                          value.max === null || value.max === 0 ? "" : value.max
                        }
                        onChange={(event) => {
                          onChange({
                            min: value.min,
                            max:
                              event.target.value === ""
                                ? null
                                : Number(event.target.value),
                          });
                        }}
                        onKeyDown={preventNonNumericValueWhenKeyDown}
                        onPaste={preventNonNumericValueWhenPaste}
                        sx={{ width: "90px", top: "50%" }}
                        inputProps={{
                          min: 0,
                          type: "number",
                          step: "1",
                        }}
                      />
                      ㎡以下
                    </>
                  );
                }}
              />
            </Box>
            <Box sx={{ my: 1 }}>
              <Label>築年月</Label>
              <Stack spacing={2}>
                <Controller
                  control={reactHookForm.control}
                  name={"buildDateRange"}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <DateRangePicker
                        startDateLabel="以降"
                        startDate={value[0]}
                        onChangeStartDate={(date) => {
                          onChange([date, value[1]]);
                        }}
                        endDateLabel="以前"
                        endDate={value[1]}
                        maxDate={maxDate}
                        onChangeEndDate={(date) => {
                          onChange([value[0], date]);
                        }}
                        inputFormat="yyyy年"
                        mask="____年"
                        views={["year"]}
                      />
                    );
                  }}
                />
              </Stack>
              <Box sx={{ my: 1 }}>
                <Label>築年数</Label>
                <Controller
                  control={reactHookForm.control}
                  name={"buildingAge"}
                  render={({ field: { value, onChange }, fieldState }) => (
                    <TextField
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      value={
                        value.min === null || value.min === 0 ? "" : value.min
                      }
                      label="年以上"
                      onChange={(event) => {
                        onChange({
                          min:
                            event.target.value === ""
                              ? null
                              : Number(event.target.value),
                          max: value.max,
                        });
                      }}
                      onKeyDown={preventNonNumericValueWhenKeyDown}
                      onPaste={preventNonNumericValueWhenPaste}
                      sx={{ width: "120px", top: "50%", mr: 1 }}
                      inputProps={{
                        min: 0,
                        type: "number",
                        step: "10",
                      }}
                    />
                  )}
                />
                <Controller
                  control={reactHookForm.control}
                  name="buildingAge"
                  render={({ field: { value, onChange }, fieldState }) => (
                    <TextField
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      value={
                        value.max === null || value.max === 0 ? "" : value.max
                      }
                      id="buildingAgeMax"
                      label="年以下"
                      onChange={(event) => {
                        onChange({
                          min: value.min,
                          max:
                            event.target.value === ""
                              ? null
                              : Number(event.target.value),
                        });
                      }}
                      onKeyDown={preventNonNumericValueWhenKeyDown}
                      onPaste={preventNonNumericValueWhenPaste}
                      sx={{ width: "120px", top: "50%" }}
                      inputProps={{
                        min: 0,
                        type: "number",
                        step: "10",
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      <Box
        sx={{
          position: "sticky",
          paddingBottom: 1,
          bottom: 0,
          backgroundColor: theme.palette.background.default,
          zIndex: "10",
        }}
      >
        <Button
          label={"検索"}
          onClick={() => {
            (async () => {
              const submit = reactHookForm.handleSubmit(
                async (data) => {
                  if (
                    !hasMapViewingPermission ||
                    !reactHookForm.formState.isValid
                  ) {
                    return;
                  }
                  const result = await getMapRealEstateSearchAPI(
                    apiClient,
                    data
                  );
                  if (
                    result?.status === HttpStatusCode.Ok &&
                    !result?.timeout
                  ) {
                    if (
                      result.data?.count &&
                      result.data.count >= MAX_SEARCH_RESULT_COUNT
                    ) {
                      toast.info(
                        `検索結果の件数は${result.data.count.toLocaleString()}件です。${MAX_SEARCH_RESULT_COUNT.toLocaleString()}件より少なくなるように条件を絞り込んでください。`
                      );
                      return;
                    }
                    const newSearchResult =
                      result.data?.list?.map((item, index) => {
                        return {
                          id: index + 1,
                          landId: item.landId,
                          buildingId: item.buildingId,
                          buildingName: item.buildingName,
                          realEstateType: item.realEstateType,
                          selected: false,
                          bookType: getBookTypeFromRealEstateTypeLabel(
                            item.realEstateType
                          ),
                          prefName: item.prefName,
                          cityName: item.cityName,
                          location: item.location,
                          chibanKaokuNumber: item.chibanKaokuNumber,
                          address: item.address,
                          area: item.area,
                          latitude: item.latitude,
                          longitude: item.longitude,
                          isFeedOrigin: false,
                          rawData: undefined,
                        };
                      }) || [];
                    if (newSearchResult.length) {
                      setSearchResult(newSearchResult);
                      setViewLatLng(
                        new LatLng(
                          newSearchResult[0].latitude,
                          newSearchResult[0].longitude
                        )
                      );
                      toast.info("検索結果が表示されました");
                    } else {
                      toast.info("検索結果がありません");
                    }
                  } else if (
                    result?.status === HttpStatusCode.Ok &&
                    result?.timeout
                  ) {
                    toast.info(
                      "該当するデータが多すぎて処理できませんでした。より詳細な条件で再度検索してください。"
                    );
                    return;
                  } else if (
                    result?.status &&
                    result.status >= HttpStatusCode.InternalServerError
                  ) {
                    toast.error("予期せぬエラーが発生しました");
                    Sentry.captureException(
                      "不動産検索APIの呼び出しに失敗しました"
                    );
                  } else {
                    toast.info("再度検索を実行してください");
                  }
                },
                (errors, event) => {
                  console.log("バリデーショ失敗", errors);
                  Sentry.captureException(
                    "不動産検索APIの呼び出しに失敗しました"
                  );
                }
              );
              await submit();
            })();
          }}
          size={SizingWrapperStyle.INHERIT}
          variant={ButtonVariantOption.Contained}
          disabled={
            !hasMapViewingPermission || !reactHookForm.formState.isValid
          }
          hasLoading
          loading={reactHookForm.formState.isSubmitting}
        />
      </Box>
    </Box>
  );

  return (
    <Drawer
      open={open}
      hideBackdrop={true}
      onClose={toggleDrawer(false)}
      variant={"persistent"}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          backgroundColor: theme.palette.background.default,
          zIndex: "10",
        }}
      >
        <DrawerHeader>
          <Toolbar />
          <Tooltip title={"クリックで閉じる"} placement={"left"}>
            <IconButton onClick={toggleDrawer(false)}>
              <KeyboardDoubleArrowLeftIcon />
            </IconButton>
          </Tooltip>
        </DrawerHeader>
        <Divider />
      </Box>
      {DrawerContent}
    </Drawer>
  );
};
